/**
 * 指挥台：路网 / 地标 / 建筑 / 等高线矢量叠加（经 A 代理 MVT）
 * - WGS84 底图：直接 vector MVT
 * - 高德/谷歌（GCJ）：框选范围内拉 MVT → GeoJSON 纠偏后叠加（与坐标点同一套 toMapLngLat）
 * - 切底图后可通过 rehydrateMapLayerOverlays 恢复，不丢叠加层
 */
import siteConfig from '../config/site-config.js';
import mapboxgl from 'mapbox-gl';
import {
  needsGcjDisplayCorrection,
  toMapLngLat,
  fromMapLngLat,
  getActiveBasemapIdForCoord
} from './dashboard/basemapCoord';
import { fetchMapLayerGcjGeoJSON } from './mapLayerGcjGeojson';
import {
  normalizeContourLodSettings,
  resolveContourLodForHeight,
  DEFAULT_CONTOUR_LOD
} from './contourLodSettings';

/** layerId → { layer, options }，切 style 后靠此恢复 */
const overlayRegistry = new Map();

function apiBase() {
  return String(siteConfig.getApiUrl() || '/api').replace(/\/$/, '');
}

function sourceIdOf(layerId) {
  return `zhxt-map-layer-${layerId}`;
}

function lineLayerIdOf(layerId) {
  return `${sourceIdOf(layerId)}-line`;
}

function fillLayerIdOf(layerId) {
  return `${sourceIdOf(layerId)}-fill`;
}

function circleLayerIdOf(layerId) {
  return `${sourceIdOf(layerId)}-circle`;
}

function labelLayerIdOf(layerId) {
  return `${sourceIdOf(layerId)}-label`;
}

function labelAlongLayerIdOf(layerId) {
  return `${sourceIdOf(layerId)}-label-along`;
}

function labelPtsSourceIdOf(layerId) {
  return `${sourceIdOf(layerId)}-elev-pts`;
}

function contourLabelLayerIdsOf(layerId) {
  return [labelLayerIdOf(layerId), labelAlongLayerIdOf(layerId)];
}

function contourLabelRootIdOf(layerId) {
  return `zhxt-contour-elev-${layerId}`;
}

function ensureContourLabelCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('zhxt-contour-elev-css')) return;
  const style = document.createElement('style');
  style.id = 'zhxt-contour-elev-css';
  style.textContent = `
.zhxt-contour-elev-mark{pointer-events:none;}
.zhxt-contour-elev{display:block;color:#fff;font-weight:700;font-family:system-ui,-apple-system,sans-serif;white-space:nowrap;line-height:1;letter-spacing:.02em;text-shadow:0 0 3px #000,0 1px 2px #000,1px 0 2px #000,-1px 0 2px #000,0 -1px 2px #000;}
.zhxt-contour-elev.is-major{font-size:13px;}
.zhxt-contour-elev.is-minor{font-size:10px;opacity:.92;}
`;
  document.head.appendChild(style);
}

function clearContourElevMarkers(markers) {
  (markers || []).forEach((m) => {
    try { m.remove(); } catch (_) { /* ignore */ }
  });
  if (markers) markers.length = 0;
}

function syncContourElevMarkers(map, markers, labels) {
  ensureContourLabelCss();
  while (markers.length > labels.length) {
    const m = markers.pop();
    try { m.remove(); } catch (_) { /* ignore */ }
  }
  for (let i = 0; i < labels.length; i += 1) {
    const p = labels[i];
    const lngLat = [Number(p.lng), Number(p.lat)];
    if (!markers[i]) {
      const wrap = document.createElement('div');
      wrap.className = 'zhxt-contour-elev-mark';
      const span = document.createElement('span');
      span.className = 'zhxt-contour-elev is-minor';
      wrap.appendChild(span);
      markers[i] = new mapboxgl.Marker({
        element: wrap,
        anchor: 'center',
        pitchAlignment: 'viewport',
        rotationAlignment: 'viewport'
      }).setLngLat(lngLat).addTo(map);
    } else {
      markers[i].setLngLat(lngLat);
    }
    const span = markers[i].getElement()?.querySelector?.('.zhxt-contour-elev');
    if (span) {
      span.textContent = `${p.elev}`;
      span.className = `zhxt-contour-elev ${p.major ? 'is-major' : 'is-minor'}`;
    }
  }
}

function styleLayerIdsOf(layerId) {
  return [
    lineLayerIdOf(layerId),
    fillLayerIdOf(layerId),
    circleLayerIdOf(layerId),
    ...contourLabelLayerIdsOf(layerId)
  ];
}

function renderTypeOf(kind) {
  if (kind === 'poi') return 'circle';
  if (kind === 'building') return 'fill';
  return 'line';
}

function tileUrlOf(layerId) {
  const token = localStorage.getItem('token') || '';
  return `${apiBase()}/map-layers/layers/${layerId}/tile/{z}/{x}/{y}.mvt?access_token=${encodeURIComponent(token)}`;
}

/** 规范化 clip：{west,south,east,north} 或 [w,s,e,n] */
export function normalizeClipBounds(clip) {
  if (!clip) return null;
  if (Array.isArray(clip) && clip.length >= 4) {
    const [west, south, east, north] = clip.map(Number);
    if (![west, south, east, north].every(Number.isFinite)) return null;
    if (!(east > west && north > south)) return null;
    return { west, south, east, north };
  }
  const west = Number(clip.west);
  const south = Number(clip.south);
  const east = Number(clip.east);
  const north = Number(clip.north);
  if (![west, south, east, north].every(Number.isFinite)) return null;
  if (!(east > west && north > south)) return null;
  return { west, south, east, north };
}

/** 业务 WGS84 框选 → 当前底图显示坐标（within / fitBounds 用） */
export function clipBoundsForDisplay(clip, map) {
  const b = normalizeClipBounds(clip);
  if (!b) return null;
  if (!needsGcjDisplayCorrection(getActiveBasemapIdForCoord(map), map)) return b;
  const sw = toMapLngLat(b.west, b.south, undefined, map) || [b.west, b.south];
  const ne = toMapLngLat(b.east, b.north, undefined, map) || [b.east, b.north];
  const west = Math.min(sw[0], ne[0]);
  const east = Math.max(sw[0], ne[0]);
  const south = Math.min(sw[1], ne[1]);
  const north = Math.max(sw[1], ne[1]);
  if (!(east > west && north > south)) return b;
  return { west, south, east, north };
}

/** Mapbox GL filter：要素几何完全落在框选多边形内才显示 */
export function clipBoundsToWithinFilter(clip, map = null) {
  const b = map ? clipBoundsForDisplay(clip, map) : normalizeClipBounds(clip);
  if (!b) return null;
  const { west, south, east, north } = b;
  const padX = Math.max((east - west) * 0.02, 0.0005);
  const padY = Math.max((north - south) * 0.02, 0.0005);
  const w = west - padX;
  const e = east + padX;
  const s = south - padY;
  const n = north + padY;
  return [
    'within',
    {
      type: 'Polygon',
      coordinates: [[
        [w, s],
        [e, s],
        [e, n],
        [w, n],
        [w, s]
      ]]
    }
  ];
}

function combineFilters(...parts) {
  const ok = parts.filter(Boolean);
  if (!ok.length) return null;
  if (ok.length === 1) return ok[0];
  return ['all', ...ok];
}

const CONTOUR_DISPLAY_INTERVALS = [5, 10, 20, 30];

/** 切片原始等高距（未知时按 5m，允许在其上抽稀到 10/20/30） */
export function bakedContourIntervalOf(layer) {
  const n = Number(layer?.styleHint?.contourInterval ?? layer?.style_hint?.contourInterval);
  return CONTOUR_DISPLAY_INTERVALS.includes(n) ? n : 5;
}

/** 计曲线间距：5/10m→50m，20m→100m，30m→60m；其余（含 LOD）每条可见线都当计曲线 */
export function contourIndexStepOf(interval) {
  const n = Number(interval) || 10;
  if (n === 20) return 100;
  if (n === 30) return 60;
  if (n === 5 || n === 10) return 50;
  return Math.max(1, n);
}

function contourElevExpr() {
  return [
    'to-number',
    ['coalesce', ['get', 'elev'], ['get', 'ELEV'], ['get', 'height'], -9999]
  ];
}

function isContourIndexElev(elev, interval) {
  if (elev == null) return false;
  return Math.round(elev) % contourIndexStepOf(interval) === 0;
}

/** 间曲线是否标数字：5/10m 每 20m；20m 每 40m；30m 全部间曲线 */
function isContourMinorLabeled(elev, interval) {
  if (elev == null || isContourIndexElev(elev, interval)) return false;
  const n = Number(interval) || 10;
  if (n === 5 || n === 10) return Math.round(elev) % 20 === 0;
  if (n === 20) return Math.round(elev) % 40 === 0;
  return true;
}

/** 可显示间距：须 ≥ 切片间距，且为切片间距的整数倍 */
export function allowedContourDisplayIntervals(bakedInterval) {
  const baked = CONTOUR_DISPLAY_INTERVALS.includes(Number(bakedInterval))
    ? Number(bakedInterval)
    : 5;
  return CONTOUR_DISPLAY_INTERVALS.filter((i) => i >= baked && i % baked === 0);
}

export function normalizeContourDisplayInterval(interval, bakedInterval) {
  const allowed = allowedContourDisplayIntervals(bakedInterval);
  const n = Number(interval);
  if (allowed.includes(n)) return n;
  return allowed[0] || 10;
}

/** 抽稀间距对齐切片距（50m 在 20m 切片上会提到 60m） */
export function snapContourIntervalToBaked(interval, bakedInterval) {
  const baked = CONTOUR_DISPLAY_INTERVALS.includes(Number(bakedInterval))
    ? Number(bakedInterval)
    : 5;
  const n = Number(interval);
  if (!Number.isFinite(n) || n <= 0) return baked;
  return Math.max(baked, Math.ceil(n / baked) * baked);
}

/** 只保留 elev 为 displayInterval 整数倍的等高线（抽稀显示，不是只改标注） */
export function contourElevStepFilter(displayInterval) {
  const step = Math.max(1, Number(displayInterval) || 10);
  return [
    'all',
    ['any', ['has', 'elev'], ['has', 'ELEV'], ['has', 'height']],
    ['==', ['%', ['round', contourElevExpr()], step], 0]
  ];
}

function contourCombinedFilter(clip, map, displayInterval) {
  const clipFilter = clip ? clipBoundsToWithinFilter(clip, map) : null;
  const elevFilter = contourElevStepFilter(displayInterval);
  return combineFilters(clipFilter, elevFilter);
}

function contourIndexMatchExpr(interval) {
  return ['==', ['%', ['round', contourElevExpr()], contourIndexStepOf(interval)], 0];
}

function syncContourOverlayStyle(map, layerId) {
  if (!map || layerId == null) return false;
  const key = Number(layerId) || layerId;
  const rec = overlayRegistry.get(key);
  const iv = rec?.options?.displayInterval ?? bakedContourIntervalOf(rec?.layer);
  const clip = rec?.options?.clipBounds || null;
  const filter = contourCombinedFilter(clip, map, iv);
  const isIndex = contourIndexMatchExpr(iv);
  let applied = false;
  const lineId = lineLayerIdOf(layerId);
  if (map.getLayer(lineId)) {
    map.setFilter(lineId, filter);
    applyContourLinePaint(map, layerId);
    applied = true;
  }
  const sizeExpr = ['case', isIndex, 14, 11];
  const haloExpr = ['case', isIndex, 1.9, 1.5];
  contourLabelLayerIdsOf(layerId).forEach((lid) => {
    if (!map.getLayer(lid)) return;
    map.setFilter(lid, filter);
    try { map.setLayoutProperty(lid, 'text-size', sizeExpr); } catch (_) { /* ignore */ }
    try { map.setPaintProperty(lid, 'text-halo-width', haloExpr); } catch (_) { /* ignore */ }
    applied = true;
  });
  contourLabelSyncs.get(key)?.refresh?.();
  return applied;
}

export function applyMapLayerClipFilter(map, layerId, clip) {
  if (!map || layerId == null) return false;
  const key = Number(layerId) || layerId;
  const prev = overlayRegistry.get(key);
  const kind = prev?.layer?.kind;
  if (prev) {
    overlayRegistry.set(key, {
      ...prev,
      options: {
        ...prev.options,
        clipBounds: clip ? (normalizeClipBounds(clip) || prev.options?.clipBounds) : null
      }
    });
  }
  if (kind === 'contour') {
    return syncContourOverlayStyle(map, layerId);
  }
  const filter = clip ? clipBoundsToWithinFilter(clip, map) : null;
  let applied = false;
  styleLayerIdsOf(layerId).forEach((lid) => {
    if (!map.getLayer(lid)) return;
    map.setFilter(lid, filter);
    applied = true;
  });
  return applied;
}

/** 运行时切换某条等高线的显示间距（抽稀线，并同步标注） */
export function applyMapLayerContourInterval(map, layerId, displayInterval, clip = null, extra = {}) {
  if (!map || layerId == null) return false;
  const key = Number(layerId) || layerId;
  const prev = overlayRegistry.get(key);
  const baked = bakedContourIntervalOf(prev?.layer);
  const asBase = extra.asBase === true;
  const iv = asBase
    ? normalizeContourDisplayInterval(displayInterval, baked)
    : snapContourIntervalToBaked(displayInterval, baked);
  const clipUse = clip != null
    ? normalizeClipBounds(clip)
    : (prev?.options?.clipBounds || null);
  if (prev) {
    overlayRegistry.set(key, {
      ...prev,
      options: {
        ...prev.options,
        displayInterval: iv,
        ...(asBase ? { baseDisplayInterval: iv } : {}),
        clipBounds: clipUse || prev.options?.clipBounds
      }
    });
  }
  return syncContourOverlayStyle(map, layerId);
}

let contourLodConfig = normalizeContourLodSettings(DEFAULT_CONTOUR_LOD);
let contourLodMap = null;
let contourLodOnCamera = null;
let contourLodTimer = null;

function estimateCameraHeightMeters(map) {
  try {
    const zoom = map.getZoom?.();
    const lat = map.getCenter?.()?.lat;
    const screenH = map.getCanvas?.()?.clientHeight || 800;
    if (!Number.isFinite(zoom)) return null;
    const R = 6378137;
    const initialResolution = (2 * Math.PI * R) / 256;
    const resolution = initialResolution / Math.pow(2, zoom);
    return Math.round((resolution * screenH) / 2);
  } catch (_) {
    return null;
  }
}

function setContourLayerVisibility(map, layerId, visible) {
  const vis = visible ? 'visible' : 'none';
  [lineLayerIdOf(layerId), ...contourLabelLayerIdsOf(layerId)].forEach((lid) => {
    try {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    } catch (_) { /* ignore */ }
  });
  const root = contourLabelSyncs.get(Number(layerId) || layerId);
  (root?.markers || []).forEach((m) => {
    try {
      const el = m.getElement?.();
      if (el) el.style.display = visible ? '' : 'none';
    } catch (_) { /* ignore */ }
  });
}

export function setContourLodConfig(cfg) {
  contourLodConfig = normalizeContourLodSettings(cfg);
  if (contourLodMap) applyContourLodToRegistry(contourLodMap);
}

export function applyContourLodToRegistry(map) {
  if (!map) return;
  const height = estimateCameraHeightMeters(map);
  overlayRegistry.forEach((entry, key) => {
    if (entry?.layer?.kind !== 'contour') return;
    if (!map.getLayer(lineLayerIdOf(key))) return;
    const baked = bakedContourIntervalOf(entry.layer);
    const base = entry.options?.baseDisplayInterval
      ?? entry.options?.displayInterval
      ?? baked;
    const lod = resolveContourLodForHeight(height, contourLodConfig);
    if (lod.mode === 'hide') {
      setContourLayerVisibility(map, key, false);
      return;
    }
    setContourLayerVisibility(map, key, true);
    const iv = lod.mode === 'interval'
      ? snapContourIntervalToBaked(lod.interval, baked)
      : normalizeContourDisplayInterval(base, baked);
    if (Number(entry.options?.displayInterval) === iv) {
      return;
    }
    applyMapLayerContourInterval(map, key, iv, entry.options?.clipBounds || null);
  });
}

function ensureContourLodCameraSync(map) {
  if (!map || contourLodMap === map) {
    if (map) applyContourLodToRegistry(map);
    return;
  }
  if (contourLodMap && contourLodOnCamera) {
    try { contourLodMap.off('zoomend', contourLodOnCamera); } catch (_) { /* ignore */ }
    try { contourLodMap.off('moveend', contourLodOnCamera); } catch (_) { /* ignore */ }
    try { contourLodMap.off('pitchend', contourLodOnCamera); } catch (_) { /* ignore */ }
  }
  contourLodMap = map;
  contourLodOnCamera = () => {
    if (contourLodTimer) clearTimeout(contourLodTimer);
    contourLodTimer = setTimeout(() => applyContourLodToRegistry(map), 90);
  };
  try {
    map.on('zoomend', contourLodOnCamera);
    map.on('moveend', contourLodOnCamera);
    map.on('pitchend', contourLodOnCamera);
  } catch (_) { /* ignore */ }
  applyContourLodToRegistry(map);
}

/** 计曲线亮黄、间曲线半透明白（与谷歌地球 KML 一致） */
function contourLinePaint(displayInterval, styleHint = {}, overlayOpacity = 1) {
  const o = Math.min(1, Math.max(0, Number(overlayOpacity)));
  const indexStep = contourIndexStepOf(displayInterval);
  const isIndex = ['==', ['%', ['round', contourElevExpr()], indexStep], 0];
  const indexColor = styleHint.indexLineColor || '#FFC800';
  const minorColor = styleHint.minorLineColor || '#ffffff';
  const indexW = styleHint.indexLineWidth != null ? Number(styleHint.indexLineWidth) : 1.85;
  const minorW = styleHint.minorLineWidth != null ? Number(styleHint.minorLineWidth) : 0.85;
  const minorA = styleHint.minorLineOpacity != null ? Number(styleHint.minorLineOpacity) : 0.33;
  return {
    'line-color': ['case', isIndex, indexColor, minorColor],
    'line-width': ['case', isIndex, indexW, minorW],
    'line-opacity': ['case', isIndex, o, Math.max(0, Math.min(1, o * minorA))]
  };
}

function applyContourLinePaint(map, layerId) {
  if (!map || layerId == null) return;
  const key = Number(layerId) || layerId;
  const rec = overlayRegistry.get(key);
  const lineId = lineLayerIdOf(layerId);
  if (!map.getLayer(lineId)) return;
  const iv = rec?.options?.displayInterval ?? bakedContourIntervalOf(rec?.layer);
  const o = rec?.options?.opacity != null ? Number(rec.options.opacity) : 1;
  const paint = contourLinePaint(iv, rec?.layer?.styleHint || {}, o);
  try {
    map.setPaintProperty(lineId, 'line-color', paint['line-color']);
    map.setPaintProperty(lineId, 'line-width', paint['line-width']);
    map.setPaintProperty(lineId, 'line-opacity', paint['line-opacity']);
  } catch (_) { /* ignore */ }
}

function paintLine(kind, styleHint = {}, displayInterval) {
  if (kind === 'contour') {
    return contourLinePaint(displayInterval, styleHint, 1);
  }
  if (kind === 'building') {
    return {
      'line-color': styleHint.lineColor || '#64748b',
      'line-width': 0.6,
      'line-opacity': styleHint.lineOpacity != null ? styleHint.lineOpacity : 0.7
    };
  }
  return {
    'line-color': styleHint.lineColor || '#2563eb',
    'line-width': [
      'interpolate', ['linear'], ['zoom'],
      10, 0.6,
      12, 1.2,
      14, 2.0,
      16, 2.8
    ],
    'line-opacity': styleHint.lineOpacity != null ? styleHint.lineOpacity : 0.9
  };
}

/** 等高线数字：计曲线大字、间曲线小字，沿线等距取样 */
const CONTOUR_LABEL_OPACITY = 1;
const contourLabelSyncs = new Map();

function contourLabelOpacity(styleHint = {}) {
  if (styleHint.labelOpacity != null && Number.isFinite(Number(styleHint.labelOpacity))) {
    return Math.min(0.95, Math.max(0.55, Number(styleHint.labelOpacity)));
  }
  return CONTOUR_LABEL_OPACITY;
}

function contourElevOfProps(props) {
  const n = Number(
    props?.elev ?? props?.ELEV ?? props?.ele ?? props?.ELE
    ?? props?.height ?? props?.elevation ?? props?.Z ?? props?.z
  );
  return Number.isFinite(n) ? Math.round(n) : null;
}

function lineCoordsOfFeature(feature) {
  const g = feature?.geometry;
  if (!g) return [];
  if (g.type === 'LineString' && Array.isArray(g.coordinates)) return [g.coordinates];
  if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) return g.coordinates;
  return [];
}

function projectPolyline(map, coords) {
  const pts = [];
  for (let i = 0; i < coords.length; i += 1) {
    const c = coords[i];
    if (!c || c.length < 2) continue;
    let p;
    try {
      p = map.project([Number(c[0]), Number(c[1])]);
    } catch (_) {
      continue;
    }
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    pts.push({ x: p.x, y: p.y, lng: Number(c[0]), lat: Number(c[1]) });
  }
  return pts;
}

function polylineScreenLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) {
    len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  return len;
}

function pointAlongPolyline(pts, target) {
  if (!pts.length) return null;
  if (target <= 0) return pts[0];
  let acc = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (acc + d >= target) {
      const t = d > 1e-6 ? (target - acc) / d : 0;
      return {
        x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t,
        y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t,
        lng: pts[i - 1].lng + (pts[i].lng - pts[i - 1].lng) * t,
        lat: pts[i - 1].lat + (pts[i].lat - pts[i - 1].lat) * t
      };
    }
    acc += d;
  }
  return pts[pts.length - 1];
}

function screenDist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function inMapViewport(pt, width, height, pad) {
  return pt.x >= pad && pt.y >= pad && pt.x <= width - pad && pt.y <= height - pad;
}

/** 同一高度、端点靠近的线段拼成一条等高线 */
function clusterContourPieces(pieces) {
  const n = pieces.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const unite = (a, b) => {
    const pa = find(a);
    const pb = find(b);
    if (pa !== pb) parent[pa] = pb;
  };
  const byElev = new Map();
  pieces.forEach((piece, i) => {
    const list = byElev.get(piece.elev) || [];
    list.push(i);
    byElev.set(piece.elev, list);
  });
  const joinPx = 56;
  byElev.forEach((idxs) => {
    for (let a = 0; a < idxs.length; a += 1) {
      const pa = pieces[idxs[a]].pts;
      if (!pa.length) continue;
      const aEnds = [pa[0], pa[pa.length - 1]];
      for (let b = a + 1; b < idxs.length; b += 1) {
        const pb = pieces[idxs[b]].pts;
        if (!pb.length) continue;
        const bEnds = [pb[0], pb[pb.length - 1]];
        let close = false;
        for (let i = 0; i < aEnds.length && !close; i += 1) {
          for (let j = 0; j < bEnds.length; j += 1) {
            if (screenDist(aEnds[i], bEnds[j]) <= joinPx) {
              close = true;
              break;
            }
          }
        }
        if (close) unite(idxs[a], idxs[b]);
      }
    }
  });
  const groups = new Map();
  pieces.forEach((piece, i) => {
    const root = find(i);
    const list = groups.get(root) || [];
    list.push(piece);
    groups.set(root, list);
  });
  return Array.from(groups.values());
}

function chainContourParts(parts) {
  const unused = parts.filter((p) => p.pts.length >= 2).sort((a, b) => b.len - a.len);
  if (!unused.length) return [];
  const chain = unused.shift().pts.slice();
  let guard = 0;
  while (unused.length && guard < 120) {
    guard += 1;
    const head = chain[0];
    const tail = chain[chain.length - 1];
    let bestI = -1;
    let bestD = 80;
    let flip = false;
    let atHead = false;
    unused.forEach((part, i) => {
      const a = part.pts[0];
      const b = part.pts[part.pts.length - 1];
      const dTailA = screenDist(tail, a);
      const dTailB = screenDist(tail, b);
      const dHeadA = screenDist(head, a);
      const dHeadB = screenDist(head, b);
      if (dTailA < bestD) { bestD = dTailA; bestI = i; flip = false; atHead = false; }
      if (dTailB < bestD) { bestD = dTailB; bestI = i; flip = true; atHead = false; }
      if (dHeadA < bestD) { bestD = dHeadA; bestI = i; flip = true; atHead = true; }
      if (dHeadB < bestD) { bestD = dHeadB; bestI = i; flip = false; atHead = true; }
    });
    if (bestI < 0) break;
    const next = unused.splice(bestI, 1)[0].pts.slice();
    if (flip) next.reverse();
    if (atHead) chain.unshift(...next);
    else chain.push(...next);
  }
  return chain;
}

function tangentAngleDeg(chain, dist) {
  const len = polylineScreenLength(chain);
  const a = pointAlongPolyline(chain, Math.max(0, dist - 10));
  const b = pointAlongPolyline(chain, Math.min(len, dist + 10));
  if (!a || !b) return 0;
  let deg = Math.atan2(b.y - a.y, b.x - a.x) * (180 / Math.PI);
  if (deg > 90 || deg < -90) deg += 180;
  if (deg > 180) deg -= 360;
  return deg;
}

function sampleEvenAlongChain(chain, spacingPx, phase) {
  const len = polylineScreenLength(chain);
  const out = [];
  if (len < 22 || chain.length < 2) return out;
  const addAt = (d) => {
    const p = pointAlongPolyline(chain, d);
    if (p) out.push({ ...p, angle: tangentAngleDeg(chain, d) });
  };
  if (len < Math.max(72, spacingPx * 0.55)) {
    addAt(len * 0.5);
    return out;
  }
  const start = (((phase % 1) + 1) % 1) * spacingPx * 0.45 + spacingPx * 0.18;
  for (let d = start; d < len - 10; d += spacingPx) addAt(d);
  if (!out.length) addAt(len * 0.5);
  return out;
}

function overlayKeyFromLineId(lineId) {
  const keyRaw = String(lineId).replace(/^zhxt-map-layer-/, '').replace(/-line$/, '');
  return Number(keyRaw) || keyRaw;
}

function collectContourLineFeatures(map, lineId) {
  let features = [];
  try {
    features = map.queryRenderedFeatures({ layers: [lineId] }) || [];
  } catch (_) {
    features = [];
  }
  if (features.length) return features;
  try {
    const layer = map.getLayer(lineId);
    if (!layer) return [];
    const q = layer['source-layer'] ? { sourceLayer: layer['source-layer'] } : undefined;
    const all = map.querySourceFeatures(layer.source, q) || [];
    const bounds = map.getBounds?.();
    if (!bounds) return all.slice(0, 700);
    const west = bounds.getWest();
    const east = bounds.getEast();
    const south = bounds.getSouth();
    const north = bounds.getNorth();
    const padX = (east - west) * 0.1;
    const padY = (north - south) * 0.1;
    const w = west - padX;
    const e = east + padX;
    const s = south - padY;
    const n = north + padY;
    const keyRaw = String(lineId).replace(/^zhxt-map-layer-/, '').replace(/-line$/, '');
    const rec = overlayRegistry.get(Number(keyRaw) || keyRaw);
    const step = Math.max(1, Number(rec?.options?.displayInterval) || 10);
    const out = [];
    for (let i = 0; i < all.length && out.length < 700; i += 1) {
      const feature = all[i];
      const elev = contourElevOfProps(feature.properties);
      if (elev == null) continue;
      if (Math.round(elev) % step !== 0) continue;
      const lines = lineCoordsOfFeature(feature);
      let hit = false;
      for (let j = 0; j < lines.length && !hit; j += 1) {
        const coords = lines[j] || [];
        for (let k = 0; k < coords.length; k += 1) {
          const c = coords[k];
          if (c && c[0] >= w && c[0] <= e && c[1] >= s && c[1] <= n) {
            hit = true;
            break;
          }
        }
      }
      if (hit) out.push(feature);
    }
    return out;
  } catch (_) {
    return features;
  }
}

function refreshContourElevLabels(map, lineId, layerId) {
  const key = Number(layerId) || layerId;
  const ctl = contourLabelSyncs.get(key);
  if (!ctl || !Array.isArray(ctl.markers)) return;
  if (map._removed || !map.getLayer?.(lineId)) {
    clearContourElevMarkers(ctl.markers);
    return;
  }
  const rec = overlayRegistry.get(overlayKeyFromLineId(lineId));
  const interval = Math.max(1, Number(rec?.options?.displayInterval) || 10);
  const features = collectContourLineFeatures(map, lineId);
  const canvas = map.getCanvas?.();
  const width = canvas?.clientWidth || 0;
  const height = canvas?.clientHeight || 0;
  if (width < 8 || height < 8) return;

  const pieces = [];
  const cap = Math.min(features.length, 800);
  for (let i = 0; i < cap; i += 1) {
    const feature = features[i];
    const elev = contourElevOfProps(feature.properties);
    if (elev == null) continue;
    const lines = lineCoordsOfFeature(feature);
    for (let j = 0; j < lines.length; j += 1) {
      const pts = projectPolyline(map, lines[j]);
      if (pts.length < 2) continue;
      const len = polylineScreenLength(pts);
      if (len < 6) continue;
      pieces.push({ elev, pts, len });
    }
  }

  const raw = [];
  clusterContourPieces(pieces).forEach((group) => {
    const elev = group[0]?.elev;
    if (elev == null) return;
    const major = isContourIndexElev(elev, interval);
    const chain = chainContourParts(group);
    const spacing = major ? 108 : 148;
    sampleEvenAlongChain(chain, spacing, major ? 0 : 0.45).forEach((p) => {
      if (!inMapViewport(p, width, height, 4)) return;
      raw.push({ ...p, elev, major });
    });
  });
  if (!raw.length) {
    pieces.forEach((piece) => {
      if (piece.len < 18) return;
      const d = piece.len * 0.5;
      const p = pointAlongPolyline(piece.pts, d);
      if (!p || !inMapViewport(p, width, height, 4)) return;
      raw.push({
        ...p,
        elev: piece.elev,
        major: isContourIndexElev(piece.elev, interval)
      });
    });
  }
  raw.sort((a, b) => Number(b.major) - Number(a.major));
  const labels = [];
  raw.forEach((p) => {
    if (labels.length >= 220) return;
    if (labels.some((q) => screenDist(p, q) < 34)) return;
    labels.push(p);
  });
  syncContourElevMarkers(map, ctl.markers, labels);
}

function stopContourLabelSync(layerId) {
  const key = Number(layerId) || layerId;
  const ctl = contourLabelSyncs.get(key);
  if (ctl) {
    try { ctl.stop(); } catch (_) { /* ignore */ }
    clearContourElevMarkers(ctl.markers);
    contourLabelSyncs.delete(key);
  }
}

function startContourLabelSync(map, layerId) {
  stopContourLabelSync(layerId);
  const key = Number(layerId) || layerId;
  const lineId = lineLayerIdOf(layerId);
  const contourSourceId = sourceIdOf(layerId);
  const markers = [];
  let ticking = false;
  let timer = null;
  const later = [];
  const run = () => {
    ticking = false;
    try {
      refreshContourElevLabels(map, lineId, layerId);
    } catch (_) {
      /* ignore */
    }
  };
  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(run);
  };
  const onCamera = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(schedule, 80);
  };
  const onSourceData = (e) => {
    if (!e || e.sourceId !== contourSourceId) return;
    if (e.sourceDataType && e.sourceDataType !== 'idle' && e.sourceDataType !== 'content') return;
    onCamera();
  };
  try {
    map.on('moveend', onCamera);
    map.on('zoomend', onCamera);
    map.on('pitchend', onCamera);
    map.on('rotateend', onCamera);
    map.on('sourcedata', onSourceData);
    map.once('idle', schedule);
  } catch (_) {
    /* ignore */
  }
  later.push(setTimeout(schedule, 200));
  later.push(setTimeout(schedule, 700));
  later.push(setTimeout(schedule, 1600));
  contourLabelSyncs.set(key, {
    markers,
    refresh: schedule,
    stop() {
      if (timer) clearTimeout(timer);
      later.forEach((id) => clearTimeout(id));
      try { map.off('moveend', onCamera); } catch (_) { /* ignore */ }
      try { map.off('zoomend', onCamera); } catch (_) { /* ignore */ }
      try { map.off('pitchend', onCamera); } catch (_) { /* ignore */ }
      try { map.off('rotateend', onCamera); } catch (_) { /* ignore */ }
      try { map.off('sourcedata', onSourceData); } catch (_) { /* ignore */ }
    }
  });
  schedule();
}

/** 等高线海拔标注：HTML 叠在线上，不依赖 Mapbox 字体文件 */
function addContourElevLabelLayer(map, { layerId }) {
  const labelId = labelLayerIdOf(layerId);
  const alongId = labelAlongLayerIdOf(layerId);
  const ptsId = labelPtsSourceIdOf(layerId);
  stopContourLabelSync(layerId);
  [labelId, alongId].forEach((lid) => {
    try {
      if (map.getLayer(lid)) map.removeLayer(lid);
    } catch (_) { /* ignore */ }
  });
  try {
    if (map.getSource(ptsId)) map.removeSource(ptsId);
  } catch (_) { /* ignore */ }
  startContourLabelSync(map, layerId);
}

function addStyleLayers(map, {
  id,
  kind,
  sourceId,
  sourceLayer,
  minzoom,
  maxzoom,
  styleHint,
  clipFilter,
  displayInterval,
  isGeoJson
}) {
  const render = renderTypeOf(kind);
  const srcLayerProp = isGeoJson ? {} : { 'source-layer': sourceLayer };
  const contourFilter = kind === 'contour'
    ? combineFilters(
      clipFilter,
      contourElevStepFilter(
        normalizeContourDisplayInterval(displayInterval, bakedContourIntervalOf({ styleHint }))
      )
    )
    : clipFilter;

  if (render === 'circle') {
    map.addLayer({
      id: circleLayerIdOf(id),
      type: 'circle',
      source: sourceId,
      ...srcLayerProp,
      minzoom,
      maxzoom,
      ...(clipFilter ? { filter: clipFilter } : {}),
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          12, 2.5,
          14, 4,
          16, 6
        ],
        'circle-color': styleHint.circleColor || '#e11d48',
        'circle-opacity': styleHint.circleOpacity != null ? styleHint.circleOpacity : 0.9,
        'circle-stroke-width': 0.8,
        'circle-stroke-color': '#fff'
      }
    });
    return;
  }

  if (render === 'fill') {
    map.addLayer({
      id: fillLayerIdOf(id),
      type: 'fill',
      source: sourceId,
      ...srcLayerProp,
      minzoom,
      maxzoom,
      ...(clipFilter ? { filter: clipFilter } : {}),
      paint: {
        'fill-color': styleHint.fillColor || '#94a3b8',
        'fill-opacity': styleHint.fillOpacity != null ? styleHint.fillOpacity : 0.28
      }
    });
    map.addLayer({
      id: lineLayerIdOf(id),
      type: 'line',
      source: sourceId,
      ...srcLayerProp,
      minzoom,
      maxzoom,
      ...(clipFilter ? { filter: clipFilter } : {}),
      paint: paintLine('building', styleHint)
    });
    return;
  }

  map.addLayer({
    id: lineLayerIdOf(id),
    type: 'line',
    source: sourceId,
    ...srcLayerProp,
    minzoom,
    maxzoom,
    ...(contourFilter ? { filter: contourFilter } : {}),
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: paintLine(kind, styleHint, displayInterval)
  });

  if (kind === 'contour') {
    addContourElevLabelLayer(map, {
      layerId: id,
      sourceId,
      sourceLayer,
      minzoom,
      maxzoom,
      filter: contourFilter,
      styleHint,
      displayInterval,
      isGeoJson
    });
  }
}

function resolveClipForLayer(layer, options, map = null) {
  const fromOpt = normalizeClipBounds(options.clipBounds);
  if (fromOpt) return fromOpt;
  if (Array.isArray(layer.bounds) && layer.bounds.length >= 4) {
    const fromLayer = normalizeClipBounds(layer.bounds);
    if (fromLayer) return fromLayer;
  }
  // 高德/谷歌：无框选时用当前视野（转回 WGS84）作为拉取范围
  if (map && needsGcjDisplayCorrection(getActiveBasemapIdForCoord(map), map)) {
    try {
      const b = map.getBounds?.();
      if (!b) return null;
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const a = fromMapLngLat(sw.lng, sw.lat, undefined, map) || [sw.lng, sw.lat];
      const c = fromMapLngLat(ne.lng, ne.lat, undefined, map) || [ne.lng, ne.lat];
      return normalizeClipBounds({
        west: Math.min(a[0], c[0]),
        south: Math.min(a[1], c[1]),
        east: Math.max(a[0], c[0]),
        north: Math.max(a[1], c[1])
      });
    } catch (_) {
      return null;
    }
  }
  return null;
}

function maybeFitBounds(map, flyTargetWgs, options) {
  const allowFit = options.fitBounds !== false;
  if (!allowFit || !flyTargetWgs) return;
  const display = clipBoundsForDisplay(flyTargetWgs, map) || flyTargetWgs;
  map.fitBounds(
    [[display.west, display.south], [display.east, display.north]],
    {
      padding: 60,
      duration: options.fitDuration != null ? options.fitDuration : 800
    }
  );
}

export function isMapLayerOnMap(map, layerId) {
  if (!map) return false;
  return !!(
    map.getLayer(lineLayerIdOf(layerId))
    || map.getLayer(fillLayerIdOf(layerId))
    || map.getLayer(circleLayerIdOf(layerId))
    || map.getLayer(labelLayerIdOf(layerId))
  );
}

export function removeMapLayerOverlay(map, layerId, { forget = true } = {}) {
  stopContourLabelSync(layerId);
  if (!map) {
    if (forget) overlayRegistry.delete(Number(layerId) || layerId);
    return;
  }
  styleLayerIdsOf(layerId).forEach((lid) => {
    try {
      if (map.getLayer(lid)) map.removeLayer(lid);
    } catch (_) { /* ignore */ }
  });
  const ptsId = labelPtsSourceIdOf(layerId);
  try {
    if (map.getSource(ptsId)) map.removeSource(ptsId);
  } catch (_) { /* ignore */ }
  const sid = sourceIdOf(layerId);
  try {
    if (map.getSource(sid)) map.removeSource(sid);
  } catch (_) { /* ignore */ }
  if (forget) overlayRegistry.delete(Number(layerId) || layerId);
}

/** 调整已叠加图层透明度 0~1 */
export function setMapLayerOverlayOpacity(map, layerId, opacity) {
  if (!map) return;
  const o = Math.min(1, Math.max(0, Number(opacity)));
  const lineId = lineLayerIdOf(layerId);
  const fillId = fillLayerIdOf(layerId);
  const circleId = circleLayerIdOf(layerId);
  const labelId = labelLayerIdOf(layerId);
  const key = Number(layerId) || layerId;
  const prev = overlayRegistry.get(key);
  if (prev) {
    overlayRegistry.set(key, {
      ...prev,
      options: { ...prev.options, opacity: o }
    });
  }
  if (prev?.layer?.kind === 'contour' && map.getLayer(lineId)) {
    applyContourLinePaint(map, layerId);
  } else if (map.getLayer(lineId)) {
    map.setPaintProperty(lineId, 'line-opacity', o);
  }
  if (map.getLayer(fillId)) map.setPaintProperty(fillId, 'fill-opacity', Math.min(0.55, o * 0.45));
  if (map.getLayer(circleId)) map.setPaintProperty(circleId, 'circle-opacity', o);
  if (map.getLayer(labelId) && prev?.layer?.kind !== 'contour') {
    map.setPaintProperty(labelId, 'text-opacity', Math.min(0.92, Math.max(0.5, o * 0.88)));
  }
}

export function listRegisteredMapLayerOverlays() {
  return Array.from(overlayRegistry.values()).map((e) => ({
    layer: e.layer,
    options: { ...(e.options || {}) }
  }));
}

/**
 * 切底图 / setStyle 后恢复登记中的叠加层（保留勾选意图）
 */
export async function rehydrateMapLayerOverlays(map) {
  if (!map) return { restored: 0 };
  const entries = listRegisteredMapLayerOverlays();
  if (!entries.length) return { restored: 0 };
  let restored = 0;
  for (const { layer, options } of entries) {
    try {
      // forget:false —— 保留 registry，仅清地图上残留
      removeMapLayerOverlay(map, layer.id, { forget: false });
      // eslint-disable-next-line no-await-in-loop
      await addMapLayerOverlay(map, layer, {
        ...options,
        fitBounds: false,
        skipRegistry: false
      });
      restored += 1;
    } catch (e) {
      console.warn('[map-layer] rehydrate failed', layer?.id, e?.message || e);
    }
  }
  return { restored };
}

/**
 * 内部：添加叠加（不切换）。GCJ 底图异步填充 GeoJSON。
 */
export async function addMapLayerOverlay(map, layer, options = {}) {
  if (!map || !layer?.id) throw new Error('地图或图层无效');
  const id = layer.id;
  if (layer.dryRun || layer.overlayable === false) {
    throw new Error('DRY_RUN 图层无真实瓦片，请关闭 DRY_RUN 并用 tippecanoe 重新切片后再叠加');
  }

  if (isMapLayerOnMap(map, id)) {
    removeMapLayerOverlay(map, id, { forget: false });
  }

  const kind = layer.kind || 'road';
  const sourceLayer = layer.sourceLayer || kind;
  const minzoom = layer.minzoom != null ? Number(layer.minzoom) : 10;
  // 瓦片最高级别（PMTiles/tippecanoe）；源 maxzoom 用于取瓦片
  const tileMaxzoom = layer.maxzoom != null ? Number(layer.maxzoom) : (kind === 'contour' ? 20 : 16);
  // 等高线：样式层允许 overzoom 到 22，放大后仍显示（复用最高级瓦片）
  const styleMaxzoom = kind === 'contour' ? Math.max(tileMaxzoom, 22) : tileMaxzoom;
  const maxzoom = tileMaxzoom;
  const styleHint = layer.styleHint || {};
  const clipWgs = resolveClipForLayer(layer, options, map);
  const useGcj = needsGcjDisplayCorrection(getActiveBasemapIdForCoord(map), map);
  const sid = sourceIdOf(id);
  const displayInterval = normalizeContourDisplayInterval(
    options.displayInterval,
    bakedContourIntervalOf(layer)
  );

  // within 过滤：MVT 几何为 WGS；GeoJSON-GCJ 几何为 GCJ → 用 display clip
  const clipFilter = clipWgs
    ? clipBoundsToWithinFilter(clipWgs, useGcj ? map : null)
    : null;

  if (!options.skipRegistry) {
    overlayRegistry.set(Number(id) || id, {
      layer,
      options: {
        clipBounds: clipWgs || options.clipBounds || null,
        fitBounds: false,
        opacity: options.opacity,
        displayInterval: kind === 'contour' ? displayInterval : undefined,
        baseDisplayInterval: kind === 'contour' ? displayInterval : undefined
      }
    });
  }

  if (useGcj) {
    if (!clipWgs) {
      throw new Error('高德/谷歌底图叠加矢量层前请先框选范围');
    }
    map.addSource(sid, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    addStyleLayers(map, {
      id,
      kind,
      sourceId: sid,
      sourceLayer,
      minzoom: 0,
      maxzoom: 24,
      styleHint,
      clipFilter,
      displayInterval,
      isGeoJson: true
    });
    if (options.opacity != null) setMapLayerOverlayOpacity(map, id, options.opacity);
    maybeFitBounds(map, clipWgs, options);

    const fc = await fetchMapLayerGcjGeoJSON({
      tileUrlTemplate: tileUrlOf(id),
      sourceLayer,
      clipBounds: clipWgs,
      minzoom,
      maxzoom,
      mapZoom: map.getZoom?.()
    });
    const src = map.getSource(sid);
    if (src && typeof src.setData === 'function') {
      src.setData(fc);
    }
    contourLabelSyncs.get(Number(id) || id)?.refresh?.();
    if (kind === 'contour') ensureContourLodCameraSync(map);
    return { active: true, mode: 'geojson-gcj', featureCount: fc.features?.length || 0 };
  }

  map.addSource(sid, {
    type: 'vector',
    tiles: [tileUrlOf(id)],
    minzoom,
    maxzoom
  });
  addStyleLayers(map, {
    id,
    kind,
    sourceId: sid,
    sourceLayer,
    minzoom,
    maxzoom: styleMaxzoom,
    styleHint,
    clipFilter,
    displayInterval,
    isGeoJson: false
  });
  if (options.opacity != null) setMapLayerOverlayOpacity(map, id, options.opacity);
  maybeFitBounds(map, clipWgs, options);
  if (kind === 'contour') ensureContourLodCameraSync(map);
  return { active: true, mode: 'mvt' };
}

/**
 * 开关叠加。
 * options.fitBounds=false：不飞视野
 * options.clipBounds：框选范围，只显示该范围内的要素
 */
export function toggleMapLayerOverlay(map, layer, options = {}) {
  if (!map || !layer?.id) throw new Error('地图或图层无效');
  const id = layer.id;
  // 仅以地图上是否已有 style layer 判断开关；registry 单独用于切底图恢复
  if (isMapLayerOnMap(map, id)) {
    removeMapLayerOverlay(map, id, { forget: true });
    return { active: false };
  }

  const pending = addMapLayerOverlay(map, layer, options);
  pending.catch((e) => {
    console.warn('[map-layer] overlay failed', e?.message || e);
    overlayRegistry.delete(Number(id) || id);
    try { removeMapLayerOverlay(map, id, { forget: true }); } catch (_) { /* ignore */ }
  });
  return { active: true, pending };
}
